import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { TocEntry } from './LegalLayout';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { company, legalPaths } from '../data/company';

const TOC: TocEntry[] = [
  { id: 'who-we-are', label: 'Who we are' },
  { id: 'scope', label: 'Scope of this policy' },
  { id: 'definitions', label: 'Definitions' },
  { id: 'data-we-collect', label: 'Data we collect' },
  { id: 'purposes', label: 'Purposes and legal bases' },
  { id: 'cookies', label: 'Cookies and tracking' },
  { id: 'sharing', label: 'Who we share data with' },
  { id: 'transfers', label: 'International transfers' },
  { id: 'retention', label: 'How long we keep data' },
  { id: 'your-rights', label: 'Your rights' },
  { id: 'complaints', label: 'Complaints' },
  { id: 'security', label: 'Security' },
  { id: 'children', label: "Children's privacy" },
  { id: 'external-links', label: 'Links to other sites' },
  { id: 'changes', label: 'Changes to this policy' },
  { id: 'contact', label: 'Contact us' },
];

const PrivacyPolicy: React.FC = () => {
  useDocumentTitle(
    'Privacy Policy — Parnasa Apps',
    'How Parnasa Apps collects, uses and protects personal data, under the GDPR and Italian data protection law.'
  );

  return (
    <LegalLayout
      kicker="Legal · Data protection"
      title="Privacy Policy"
      intro="How Parnasa handles personal data when you visit this website or get in touch with us — written to meet the EU General Data Protection Regulation and Italian data protection law."
      toc={TOC}
    >
      <h2 id="who-we-are">Who we are</h2>
      <p>
        {company.legalName} (&ldquo;{company.name}&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or
        &ldquo;our&rdquo;) is the <strong>data controller</strong> for the personal data described in
        this policy, within the meaning of Article 4(7) of the General Data Protection Regulation (EU)
        2016/679 (&ldquo;GDPR&rdquo;).
      </p>
      <ul>
        <li>
          <strong>Controller:</strong> {company.legalName}
        </li>
        <li>
          <strong>Registered address:</strong> {company.addressOneLine}
        </li>
        <li>
          <strong>Contact for privacy matters:</strong>{' '}
          <a href={`mailto:${company.email}`}>{company.email}</a>
        </li>
        <li>
          <strong>Establishment:</strong> Italy, European Union
        </li>
      </ul>
      <p>
        We have not appointed a Data Protection Officer, as we are not required to do so under Article
        37 GDPR. Privacy requests are handled directly by the studio at the address above.
      </p>

      <h2 id="scope">Scope of this policy</h2>
      <p>
        This policy applies to <strong>{company.url}</strong> (the &ldquo;Website&rdquo; or
        &ldquo;Service&rdquo;) and to the correspondence you send us about our services. It explains
        what we collect, why, on what legal basis, who we share it with, how long we keep it and the
        rights you can exercise.
      </p>
      <p>
        This is an informational website. It does not host user accounts, it does not sell anything,
        it does not offer files to download, and it does not run advertising or profiling.
      </p>
      <p>
        When we deliver a project for a client and process personal data on their instructions, we act
        as a <strong>processor</strong> under Article 28 GDPR. Those activities are governed by the
        data processing agreement with that client, not by this policy.
      </p>

      <h2 id="definitions">Definitions</h2>
      <ul>
        <li>
          <strong>Personal data</strong> — any information relating to an identified or identifiable
          natural person.
        </li>
        <li>
          <strong>Processing</strong> — any operation performed on personal data, such as collection,
          storage, use, disclosure or erasure.
        </li>
        <li>
          <strong>Controller</strong> — the party that determines the purposes and means of the
          processing. For this Website, that is us.
        </li>
        <li>
          <strong>Processor</strong> — a party that processes personal data on the controller&rsquo;s
          behalf, such as our hosting provider.
        </li>
        <li>
          <strong>Usage data</strong> — data generated automatically by your use of the Website, such
          as IP address, browser type and pages viewed.
        </li>
        <li>
          <strong>Cookies</strong> — small files or similar identifiers stored on your device by a
          website. See our <Link to={legalPaths.cookies}>Cookie Policy</Link>.
        </li>
        <li>
          <strong>You</strong> — the individual visiting the Website or contacting us, whether on your
          own behalf or for an organisation.
        </li>
      </ul>

      <h2 id="data-we-collect">Data we collect</h2>

      <h3>Data you give us voluntarily</h3>
      <p>
        If you email us, the message itself and anything in it becomes personal data we hold: your
        name, email address, company, and whatever you choose to tell us about your project. Please
        send only what is needed for us to reply usefully — there is no need to include confidential
        third-party information in a first message.
      </p>

      <h3>Data collected automatically</h3>
      <p>
        Like any website, ours is served by infrastructure that records technical information for
        delivery, stability and security purposes. This may include:
      </p>
      <ul>
        <li>IP address (in whole or truncated) and approximate region derived from it;</li>
        <li>date and time of the request, and the page or file requested;</li>
        <li>HTTP status, bytes transferred and response time;</li>
        <li>browser type and version, operating system and device type;</li>
        <li>referring URL, where your browser sends one.</li>
      </ul>
      <p>
        These server logs are generated by the hosting layer and are used for troubleshooting and to
        protect the Website against abuse. They are not used to build a profile of you and are not
        combined with other sources to identify you.
      </p>

      <h3>Data we do not collect</h3>
      <ul>
        <li>No special category data under Article 9 GDPR (health, beliefs, biometrics and similar).</li>
        <li>No payment card or bank details through this Website.</li>
        <li>No automated decision-making or profiling that produces legal effects for you.</li>
        <li>No purchase or sale of personal data, in any circumstances.</li>
      </ul>

      <h2 id="purposes">Purposes and legal bases</h2>
      <p>
        We only process personal data where the GDPR gives us a lawful basis for doing so (Article 6).
        In practice:
      </p>
      <div className="table-wrap">
        <table>
        <thead>
          <tr>
            <th>Purpose</th>
            <th>Data used</th>
            <th>Legal basis</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Replying to your enquiry</td>
            <td>Contact details and message content</td>
            <td>
              Legitimate interests, Art. 6(1)(f) — responding to someone who contacted us; or Art.
              6(1)(b) where you are asking us to take steps towards a contract
            </td>
          </tr>
          <tr>
            <td>Providing and securing the Website</td>
            <td>Server logs, IP address, technical data</td>
            <td>
              Legitimate interests, Art. 6(1)(f) — delivering the site reliably and preventing abuse
            </td>
          </tr>
          <tr>
            <td>Strictly necessary cookies and storage</td>
            <td>Consent record, security and load-balancing identifiers</td>
            <td>
              Art. 122 of the Italian ePrivacy implementation — exempt from consent as strictly
              necessary; Art. 6(1)(c)/(f) GDPR
            </td>
          </tr>
          <tr>
            <td>Optional cookies (preferences, analytics, marketing)</td>
            <td>Cookie identifiers and aggregated usage statistics</td>
            <td>Your consent, Art. 6(1)(a) — freely given and withdrawable at any time</td>
          </tr>
          <tr>
            <td>Managing a client project or contract</td>
            <td>Business contact details, project correspondence</td>
            <td>Performance of a contract, Art. 6(1)(b)</td>
          </tr>
          <tr>
            <td>Meeting accounting, tax and legal obligations</td>
            <td>Invoicing and contractual records</td>
            <td>Legal obligation, Art. 6(1)(c)</td>
          </tr>
          <tr>
            <td>Establishing or defending legal claims</td>
            <td>Relevant records for the matter at hand</td>
            <td>Legitimate interests, Art. 6(1)(f)</td>
          </tr>
        </tbody>
        </table>
      </div>
      <p>
        Where we rely on legitimate interests, we have weighed our interest against your rights and
        freedoms and concluded that the processing is limited, expected in the circumstances and does
        not override them. You may ask us for information about that assessment, and you may object to
        it — see <a href="#your-rights">Your rights</a>.
      </p>
      <p>
        We do not send marketing emails from this Website, and there is no newsletter sign-up. If that
        ever changes, it will be a separate, clearly labelled opt-in with an unsubscribe link in every
        message.
      </p>

      <h2 id="cookies">Cookies and tracking</h2>
      <p>
        This Website places no non-essential cookies or similar identifiers before you have given
        consent. On your first visit, a banner lets you accept all, reject all, or choose category by
        category; every non-essential category is switched off by default. Rejecting costs you nothing
        — the whole site remains available.
      </p>
      <p>
        Your choice is stored locally in your browser so we do not have to ask again on every page,
        and it expires after 180 days. You can change or withdraw it at any time through
        &ldquo;Cookie settings&rdquo; in the footer of every page.
      </p>
      <p>
        The full inventory, purposes, categories and durations are set out in our{' '}
        <Link to={legalPaths.cookies}>Cookie Policy</Link>.
      </p>

      <h2 id="sharing">Who we share data with</h2>
      <p>We do not sell, rent or trade personal data. We share it only in these situations:</p>
      <ul>
        <li>
          <strong>Service providers (processors).</strong> Hosting, content delivery, email and, if
          enabled with your consent, analytics. They act on our documented instructions under a data
          processing agreement compliant with Article 28 GDPR, and may not use the data for their own
          purposes.
        </li>
        <li>
          <strong>Professional advisers.</strong> Accountants, auditors, insurers or lawyers, where
          needed and bound by confidentiality.
        </li>
        <li>
          <strong>Public authorities.</strong> Where disclosure is required by law, or in response to a
          valid and proportionate request from a court or competent authority.
        </li>
        <li>
          <strong>Business transfers.</strong> If the studio is ever involved in a merger, acquisition
          or transfer of assets, data may pass to the acquiring party. We would inform you beforehand
          and the data would remain protected under a policy at least as protective as this one.
        </li>
        <li>
          <strong>With your consent.</strong> For anything else, we ask first.
        </li>
      </ul>
      <p>
        You may request the current list of processors and their locations by emailing{' '}
        <a href={`mailto:${company.email}`}>{company.email}</a>.
      </p>

      <h2 id="transfers">International transfers</h2>
      <p>
        We prefer providers that store data in the European Economic Area. Where a provider processes
        data outside the EEA, we rely on one of the safeguards in Chapter V GDPR — most commonly an
        adequacy decision of the European Commission (Article 45), or the Commission&rsquo;s Standard
        Contractual Clauses together with a transfer impact assessment and supplementary technical
        measures (Article 46).
      </p>
      <p>
        You may ask us for a copy of the relevant safeguard for any given transfer, and we will provide
        it in a form that respects any commercial confidentiality.
      </p>

      <h2 id="retention">How long we keep data</h2>
      <div className="table-wrap">
        <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>Retention</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Enquiry correspondence</td>
            <td>
              Up to 24 months from the last exchange, unless it becomes part of a client relationship
            </td>
          </tr>
          <tr>
            <td>Client project records</td>
            <td>For the duration of the engagement, then as needed for warranty and legal claims</td>
          </tr>
          <tr>
            <td>Accounting and tax records</td>
            <td>10 years, as required by Italian law</td>
          </tr>
          <tr>
            <td>Server and security logs</td>
            <td>Typically 30 days, longer only where an incident is under investigation</td>
          </tr>
          <tr>
            <td>Cookie consent record</td>
            <td>180 days, then you are asked again</td>
          </tr>
        </tbody>
        </table>
      </div>
      <p>
        When a retention period ends, data is deleted or irreversibly anonymised. If you ask us to
        delete your data sooner, we will do so unless a legal obligation requires us to keep it.
      </p>

      <h2 id="your-rights">Your rights</h2>
      <p>
        If you are in the EEA, the GDPR gives you the following rights over your personal data. They
        are free to exercise, and we answer without undue delay and within one month at the latest
        (extendable by two further months for complex requests, with notice to you).
      </p>
      <ul>
        <li>
          <strong>Access (Art. 15)</strong> — confirmation of whether we process your data, and a copy
          of it with information about the processing.
        </li>
        <li>
          <strong>Rectification (Art. 16)</strong> — correction of inaccurate data and completion of
          incomplete data.
        </li>
        <li>
          <strong>Erasure (Art. 17)</strong> — deletion where the data is no longer necessary, consent
          is withdrawn, or the processing was unlawful.
        </li>
        <li>
          <strong>Restriction (Art. 18)</strong> — a pause on processing while an accuracy or objection
          question is resolved.
        </li>
        <li>
          <strong>Data portability (Art. 20)</strong> — a structured, machine-readable copy of data you
          provided, where processing is based on consent or contract.
        </li>
        <li>
          <strong>Objection (Art. 21)</strong> — to processing based on legitimate interests, and at any
          time to direct marketing, which we would then stop entirely.
        </li>
        <li>
          <strong>Withdraw consent (Art. 7(3))</strong> — at any time, without affecting the lawfulness
          of processing already carried out. For cookies, use &ldquo;Cookie settings&rdquo; in the
          footer.
        </li>
        <li>
          <strong>Not to be subject to automated decision-making (Art. 22)</strong> — we do not carry
          out such processing on this Website.
        </li>
      </ul>
      <p>
        To exercise any of these, email <a href={`mailto:${company.email}`}>{company.email}</a> with
        enough detail for us to identify your data. We may ask a question or two to confirm your
        identity — solely to avoid disclosing data to the wrong person.
      </p>

      <h2 id="complaints">Complaints</h2>
      <p>
        If you believe we have handled your personal data incorrectly, please tell us first — we would
        rather fix it. You also have the right to lodge a complaint with a supervisory authority under
        Article 77 GDPR, in particular the Italian authority:
      </p>
      <ul>
        <li>
          <strong>{company.supervisoryAuthority.name}</strong> — Piazza Venezia 11, 00187 Rome, Italy ·{' '}
          <a
            href={company.supervisoryAuthority.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            garanteprivacy.it
          </a>
        </li>
        <li>
          You may alternatively complain to the supervisory authority of your EU member state of
          residence or place of work, or seek a judicial remedy.
        </li>
      </ul>

      <h2 id="security">Security</h2>
      <p>
        We apply technical and organisational measures appropriate to the risk, as required by Article
        32 GDPR: encryption in transit (HTTPS), access restricted to the people who need it, hardened
        and patched infrastructure, least-privilege credentials, and a deliberate policy of collecting
        as little as possible in the first place.
      </p>
      <p>
        No method of transmission or storage is completely secure, and we do not claim otherwise. If a
        personal data breach occurs that is likely to result in a risk to your rights, we will notify
        the supervisory authority within 72 hours as required by Article 33, and inform you where
        Article 34 applies.
      </p>

      <h2 id="children">Children&rsquo;s privacy</h2>
      <p>
        This Website is aimed at businesses and professionals, not children. We do not knowingly
        collect personal data from anyone under 14 — the age of digital consent in Italy under Article
        8 GDPR as implemented nationally. If you believe a child has sent us personal data, contact us
        and we will delete it promptly.
      </p>

      <h2 id="external-links">Links to other sites</h2>
      <p>
        Our Website may link to third-party sites we do not control. Following such a link takes you
        to a service governed by its own privacy policy, and we are not responsible for its content or
        practices. We suggest reading the policy of any site you visit.
      </p>

      <h2 id="changes">Changes to this policy</h2>
      <p>
        We may update this policy to reflect changes in our practices, our providers or the law. The
        current version is always published on this page with a &ldquo;Last updated&rdquo; date. If a
        change materially affects how we use your personal data, we will make it prominent on the
        Website and, where consent is required, ask for it again before the change takes effect.
      </p>

      <h2 id="contact">Contact us</h2>
      <p>For any question about this policy or your personal data:</p>
      <ul>
        <li>
          <strong>Email:</strong> <a href={`mailto:${company.email}`}>{company.email}</a>
        </li>
        <li>
          <strong>Post:</strong> {company.legalName}, {company.addressOneLine}
        </li>
      </ul>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
